import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Modal, Form, Button, Row, Col, Alert, Spinner } from 'react-bootstrap';
import CustomRichTextEditor from './CustomRichTextEditor';
import FileSelector from './FileSelector';
import ObjectFieldSelector from '../fieldselector/ObjectFieldSelector';
import EmailApi from '../../api/EmailApi';
import ObjectConfigApi from '../../api/ObjectConfigApi';
import './EmailSender.css';

const EmailSender = ({
  show,
  onHide,
  recordData,
  objectName,
  onEmailSent,
  currentUser,
}) => {
  const [formData, setFormData] = useState({
    to: '',
    cc: '',
    bcc: '',
    subject: '',
    body: '',
    selectedEmailFields: [], // Changed to array for multiple selection
    selectedOwner: '',
    selectedMailConfig: '',
    selectedTemplateId: '', // Template selection
    selectedLayoutId: '', // Header/footer branding layout
  });
  const [mailConfigError, setMailConfigError] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [successTimeout, setSuccessTimeout] = useState(null);
  const [emailFields, setEmailFields] = useState([]);
  const [owners, setOwners] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [loadingFields, setLoadingFields] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [emailLayouts, setEmailLayouts] = useState([]);
  const [loadingLayouts, setLoadingLayouts] = useState(false);
  const [loadingTemplateContent, setLoadingTemplateContent] = useState(false);
  const [loadingMergePreview, setLoadingMergePreview] = useState(false);
  const [showMergeFieldSelector, setShowMergeFieldSelector] = useState(false);
  const [showMergePreviewModal, setShowMergePreviewModal] = useState(false);
  const [mergePreviewContent, setMergePreviewContent] = useState({ subject: '', body: '' });
  const [mergePreviewError, setMergePreviewError] = useState(null);
  const [mergeFieldTarget, setMergeFieldTarget] = useState('body');
  const [selectedAttachments, setSelectedAttachments] = useState([]); // Array of {id, file_name} objects
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [showFileSelector, setShowFileSelector] = useState(false);
  const subjectInputRef = useRef(null);
  const bodyEditorRef = useRef(null);
  const subjectSelectionRef = useRef({ start: null, end: null });

  // Helper function to get field value from recordData structure
  const getFieldValueFromRecordData = (recordData, fieldName) => {
    if (!recordData || !fieldName) return null;

    // Check highlight section
    if (Array.isArray(recordData.highlightSection)) {
      const highlightField = recordData.highlightSection.find(
        f => f.fieldName === fieldName || f.field_name === fieldName
      );
      if (highlightField?.value) return highlightField.value;
    }

    // Check details sections
    if (Array.isArray(recordData.details)) {
      for (const section of recordData.details) {
        if (Array.isArray(section.fields)) {
          const detailField = section.fields.find(
            f => f.fieldName === fieldName || f.field_name === fieldName
          );
          if (detailField?.value) return detailField.value;
        }
      }
    }

    // Check otherHiddenFields
    if (Array.isArray(recordData.otherHiddenFields)) {
      const hiddenField = recordData.otherHiddenFields.find(
        f => f.fieldName === fieldName || f.field_name === fieldName
      );
      if (hiddenField?.value) return hiddenField.value;
    }

    // Fallback to direct property access
    return recordData[fieldName] || null;
  };

  // Fetch email fields from ObjectConfigApi
  useEffect(() => {
    const fetchEmailFieldsFromConfig = async () => {
      if (!show || !objectName) return;

      setLoadingFields(true);
      try {
        const response = await ObjectConfigApi.fetchConfigObjectByNameWithFields(objectName);

        if (response?.success && response?.data?.fields) {
          // Filter for email type fields
          const emailTypeFields = response.data.fields.filter(
            field => field.data_type === 'email' || field.data_type === 'Email'
          );

          // Extract values from recordData for these fields
          const emailFieldsWithValues = emailTypeFields.map(field => {
            const fieldValue = getFieldValueFromRecordData(recordData, field.field_name);
            return {
              field: field.field_name,
              label: field.field_label || field.field_name,
              value: fieldValue || '',
            };
          }).filter(field => field.value); // Only include fields that have values

          setEmailFields(emailFieldsWithValues);
        } else {
          // Fallback to old method if API fails
          const emailFieldsFromRecord = extractEmailFields(recordData);
          setEmailFields(emailFieldsFromRecord);
        }
      } catch (error) {
        console.error('Error fetching email fields from config:', error);
        // Fallback to old method on error
        const emailFieldsFromRecord = extractEmailFields(recordData);
        setEmailFields(emailFieldsFromRecord);
      } finally {
        setLoadingFields(false);
      }
    };

    fetchEmailFieldsFromConfig();
  }, [show, objectName, recordData]);

  useEffect(() => {
    if (show && recordData) {
      // Set default values
      setFormData(prev => ({
        ...prev,
        subject: `Re: ${objectName} - ${recordData.name || recordData.firstname || 'Record'
          }`,
        body: generateDefaultEmailBody(recordData, objectName),
      }));
    }
  }, [show, recordData, objectName]);

  useEffect(() => {
    // Reset state when modal opens
    if (show) {
      setFormData({
        to: '',
        cc: '',
        bcc: '',
        subject: '',
        body: '',
        selectedEmailFields: [],
        selectedOwner: currentUser?.id || '',
        selectedMailConfig: '', // Will be set by fetchMailConfigs if default exists
        selectedTemplateId: '',
        selectedLayoutId: '',
      });
      setError(null);
      setSuccess(false);
      setLoading(false);
      setSelectedAttachments([]);
      setUploadedFiles([]);
      fetchOwners();
      fetchMailConfigs(); // This will auto-select default config
      fetchTemplates();
      fetchEmailLayouts();
    }
  }, [show, currentUser, objectName]);

  // Reset email fields when recordData changes
  useEffect(() => {
    if (show && recordData) {
      // Generate default email body
      const defaultBody = generateDefaultEmailBody(recordData, objectName);
      setFormData(prev => ({
        ...prev,
        body: defaultBody,
      }));
    }
  }, [show, recordData, objectName]);

  // Handle body scroll when modal opens/closes
  useEffect(() => {
    if (show) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [show]);

  // Cleanup timeout on unmount or modal close
  useEffect(() => {
    return () => {
      if (successTimeout) {
        clearTimeout(successTimeout);
      }
    };
  }, [successTimeout]);

  const extractEmailFields = record => {
    const fields = [];
    if (record.email)
      fields.push({ label: 'Email', value: record.email, field: 'email' });
    if (record.work_email)
      fields.push({
        label: 'Work Email',
        value: record.work_email,
        field: 'work_email',
      });
    if (record.personal_email)
      fields.push({
        label: 'Personal Email',
        value: record.personal_email,
        field: 'personal_email',
      });
    if (record.contact_email)
      fields.push({
        label: 'Contact Email',
        value: record.contact_email,
        field: 'contact_email',
      });
    if (record.primary_email)
      fields.push({
        label: 'Primary Email',
        value: record.primary_email,
        field: 'primary_email',
      });

    // Add any other email fields from the record
    Object.keys(record).forEach(key => {
      if (
        key.toLowerCase().includes('email') &&
        record[key] &&
        !fields.find(f => f.field === key)
      ) {
        fields.push({
          label: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
          value: record[key],
          field: key,
        });
      }
    });

    return fields;
  };

  const generateDefaultEmailBody = (record, objectName) => {
    const name = record.name || record.firstname || record.company || 'there';
    return `<p>Dear ${name},</p><p><br></p><p>Thank you for your interest in our services.</p><p><br></p><p>Best regards,<br>${currentUser?.firstname || 'Your Name'
      }</p>`;
  };

  const fetchMailConfigs = async () => {
    try {
      const emailApi = new EmailApi();
      const response = await emailApi.getEmailConfigs();
      if (response.success) {
        // Only show configs that can send email (SMTP). Google/Outlook use OAuth and don't have SMTP password.
        const smtpConfigs = (response.data || []).filter(
          (config) => {
            const t = String(config.type || '').toLowerCase();
            return t === 'smtp' || t === ''; // include legacy configs with no type
          }
        );
        setConfigs(smtpConfigs);
        // Auto-select default config if available (and it's SMTP)
        const defaultConfig = smtpConfigs.find((config) => config.is_default === true);
        if (defaultConfig) {
          setFormData(prev => ({
            ...prev,
            selectedMailConfig: defaultConfig.id,
          }));
        } else if (smtpConfigs.length > 0) {
          setFormData(prev => ({
            ...prev,
            selectedMailConfig: smtpConfigs[0].id,
          }));
        }
      } else {
        console.error('Failed to fetch Configs:', response.message);
        // Fallback to current user if API fails
        setConfigs([]);
      }
    } catch (error) {
      console.error('Error fetching Configs:', error);
      // Fallback to current user if API fails
      setConfigs([]);
    }
  };
  const fetchOwners = async () => {
    try {
      const emailApi = new EmailApi();
      const response = await emailApi.getEmailUsers();
      if (response.success) {
        setOwners(response.data);
      } else {
        console.error('Failed to fetch users:', response.message);
        // Fallback to current user if API fails
        setOwners([
          {
            id: currentUser?.id,
            name: `${currentUser?.firstname || 'Current'} ${currentUser?.lastname || 'User'
              }`,
            email: currentUser?.email,
          },
        ]);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      // Fallback to current user if API fails
      setOwners([
        {
          id: currentUser?.id,
          name: `${currentUser?.firstname || 'Current'} ${currentUser?.lastname || 'User'
            }`,
          email: currentUser?.email,
        },
      ]);
    }
  };

  const fetchTemplates = async () => {
    if (!objectName) {
      setTemplates([]);
      return;
    }

    setLoadingTemplates(true);
    try {
      const emailApi = new EmailApi();
      const response = await emailApi.getEmailTemplates(objectName);
      if (response.success) {
        setTemplates(response.data || []);
      } else {
        console.error('Failed to fetch templates:', response.message);
        setTemplates([]);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
      setTemplates([]);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const fetchEmailLayouts = async () => {
    setLoadingLayouts(true);
    try {
      const emailApi = new EmailApi();
      const response = await emailApi.getEmailLayouts();
      if (response.success) {
        setEmailLayouts(response.data || []);
      } else {
        console.error('Failed to fetch email layouts:', response.message);
        setEmailLayouts([]);
      }
    } catch (error) {
      console.error('Error fetching email layouts:', error);
      setEmailLayouts([]);
    } finally {
      setLoadingLayouts(false);
    }
  };

  const layoutIdFromTemplate = (template) => {
    if (!template) return '';
    const layoutId =
      template.email_layout ||
      template.emailLayout ||
      template.layout_id ||
      '';
    return layoutId ? String(layoutId) : '';
  };

  const selectedTemplate = useMemo(() => {
    if (!formData.selectedTemplateId) return null;
    return (
      templates.find(t => String(t.id) === String(formData.selectedTemplateId)) ||
      null
    );
  }, [formData.selectedTemplateId, templates]);

  // Lock branding when the selected template already has an attached layout.
  const isBrandingLocked = Boolean(layoutIdFromTemplate(selectedTemplate));
  const selectedLayoutName = useMemo(() => {
    if (!formData.selectedLayoutId) return '';
    const layout = emailLayouts.find(
      l => String(l.id) === String(formData.selectedLayoutId)
    );
    return layout?.name || '';
  }, [formData.selectedLayoutId, emailLayouts]);

  // Memoize recordId to avoid recalculating on every render
  const recordId = useMemo(() => {
    if (!recordData) {
      return null;
    }
    // Helper function to get field value from recordData structure
    const getFieldValue = (data, fieldName) => {
      if (!data || !fieldName) return null;

      // Check highlight section
      if (Array.isArray(data.highlightSection)) {
        const highlightField = data.highlightSection.find(
          f => f.fieldName === fieldName || f.field_name === fieldName
        );
        if (highlightField?.value) return highlightField.value;
      }

      // Check details sections
      if (Array.isArray(data.details)) {
        for (const section of data.details) {
          if (Array.isArray(section.fields)) {
            const detailField = section.fields.find(
              f => f.fieldName === fieldName || f.field_name === fieldName
            );
            if (detailField?.value) return detailField.value;
          }
        }
      }

      // Check otherHiddenFields
      if (Array.isArray(data.otherHiddenFields)) {
        const hiddenField = data.otherHiddenFields.find(
          f => f.fieldName === fieldName || f.field_name === fieldName
        );
        if (hiddenField?.value) return hiddenField.value;
      }

      // Fallback to direct property access
      return data[fieldName] || null;
    };

    const id = getFieldValue(recordData, 'id') ||
      getFieldValue(recordData, 'Id') ||
      getFieldValue(recordData, 'ID') ||
      recordData.id ||
      recordData.Id ||
      recordData.ID;
    return id;
  }, [recordData]);

  const handleAttachmentSelectionChange = (selectedItems) => {
    // selectedItems is array of {id, file_name} objects
    setSelectedAttachments(selectedItems);
    console.log('EmailSender: Selected attachments updated:', selectedItems);
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    const newFiles = files.map(file => ({
      id: `upload-${Date.now()}-${Math.random()}`,
      file: file,
      name: file.name,
      size: file.size,
      type: file.type,
      isUploaded: false,
    }));
    setUploadedFiles(prev => [...prev, ...newFiles]);
    // Reset file input
    e.target.value = '';
  };

  const handleRemoveUploadedFile = (fileId) => {
    setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const loadParsedTemplateContent = async (templateId) => {
    if (!templateId || !recordId || !objectName) {
      return null;
    }

    setLoadingTemplateContent(true);
    try {
      const emailApi = new EmailApi();
      const response = await emailApi.post('/test/parse-email-template', {
        templateId,
        recordId,
        objectName,
      });

      if (response?.data?.success && response?.data?.result) {
        return {
          subject: response.data.result.subject || '',
          body: response.data.result.body || '',
        };
      }
    } catch (error) {
      console.error('Error loading parsed template content:', error);
      setError('Failed to load template content. Using template defaults.');
    } finally {
      setLoadingTemplateContent(false);
    }

    return null;
  };

  const saveSubjectSelection = () => {
    const input = subjectInputRef.current;
    if (input && typeof input.selectionStart === 'number') {
      subjectSelectionRef.current = {
        start: input.selectionStart,
        end: input.selectionEnd ?? input.selectionStart,
      };
    }
  };

  const openMergeFieldSelector = (target) => {
    if (!objectName) {
      setError('Object name is required to insert merge fields.');
      return;
    }
    if (target === 'subject') {
      saveSubjectSelection();
    } else {
      bodyEditorRef.current?.saveSelection?.();
    }
    setMergeFieldTarget(target);
    setShowMergeFieldSelector(true);
  };

  const handleMergeFieldButtonMouseDown = (target, event) => {
    // Keep focus/selection in subject or message editor when opening the picker
    event.preventDefault();
    if (target === 'subject') {
      saveSubjectSelection();
    } else {
      bodyEditorRef.current?.saveSelection?.();
    }
  };

  const insertMergeField = (fieldName) => {
    const mergeField = `{{${fieldName}}}`;

    if (mergeFieldTarget === 'subject') {
      setFormData(prev => {
        const currentSubject = prev.subject || '';
        const { start, end } = subjectSelectionRef.current;
        if (start != null && end != null) {
          const nextSubject =
            currentSubject.substring(0, start) +
            mergeField +
            currentSubject.substring(end);
          const cursor = start + mergeField.length;
          subjectSelectionRef.current = { start: cursor, end: cursor };
          setTimeout(() => {
            const input = subjectInputRef.current;
            if (input) {
              input.focus();
              input.setSelectionRange(cursor, cursor);
            }
          }, 0);
          return { ...prev, subject: nextSubject };
        }
        return { ...prev, subject: currentSubject + mergeField };
      });
      return;
    }

    if (bodyEditorRef.current?.insertTextAtSavedSelection) {
      bodyEditorRef.current.insertTextAtSavedSelection(mergeField);
    } else {
      setFormData(prev => ({
        ...prev,
        body: `${prev.body || ''}${mergeField}`,
      }));
    }
  };

  const handleMergeFieldSelected = (field) => {
    if (field?.field_name) {
      insertMergeField(field.field_name);
    }
    setShowMergeFieldSelector(false);
  };

  const handlePreviewMergeFields = async () => {
    if (!recordId || !objectName) {
      setError('Record context is required to preview merge fields.');
      return;
    }

    setShowMergePreviewModal(true);
    setLoadingMergePreview(true);
    setMergePreviewError(null);
    setMergePreviewContent({ subject: '', body: '' });

    try {
      const emailApi = new EmailApi();
      const response = await emailApi.post('/resolve-content', {
        recordId,
        objectName,
        subject: formData.subject,
        body: formData.body,
        layoutId: formData.selectedLayoutId || null,
      });

      if (response?.data?.success && response?.data?.result) {
        setMergePreviewContent({
          subject: response.data.result.subject ?? formData.subject,
          body: response.data.result.body ?? formData.body,
        });
      } else {
        throw new Error(response?.data?.message || 'Failed to resolve merge fields');
      }
    } catch (previewError) {
      const serverMessage =
        previewError.response?.data?.message ||
        previewError.response?.data?.error ||
        previewError.message;
      setMergePreviewError(serverMessage || 'Failed to preview merge fields');
    } finally {
      setLoadingMergePreview(false);
    }
  };

  const handleCloseMergePreview = () => {
    setShowMergePreviewModal(false);
    setMergePreviewError(null);
    setMergePreviewContent({ subject: '', body: '' });
  };

  const handleTemplateChange = async (templateId) => {
    if (!templateId) {
      setFormData(prev => ({
        ...prev,
        selectedTemplateId: '',
        selectedLayoutId: '',
        subject: recordData
          ? `Re: ${objectName} - ${recordData.name || recordData.firstname || 'Record'}`
          : '',
        body: recordData ? generateDefaultEmailBody(recordData, objectName) : '',
      }));
      return;
    }

    const selectedTemplate = templates.find(
      t => String(t.id) === String(templateId)
    );
    const templateLayoutId = layoutIdFromTemplate(selectedTemplate);

    setFormData(prev => ({
      ...prev,
      selectedTemplateId: templateId,
      selectedLayoutId: templateLayoutId,
    }));

    const parsedContent = await loadParsedTemplateContent(templateId);
    if (parsedContent) {
      setFormData(prev => ({
        ...prev,
        selectedTemplateId: templateId,
        selectedLayoutId: templateLayoutId,
        subject: parsedContent.subject,
        body: parsedContent.body,
      }));
      return;
    }

    if (selectedTemplate) {
      setFormData(prev => ({
        ...prev,
        selectedTemplateId: templateId,
        selectedLayoutId: templateLayoutId,
        subject: selectedTemplate.subject || prev.subject,
        body: selectedTemplate.body || prev.body,
      }));
    }
  };

  const handleLayoutChange = (layoutId) => {
    setFormData(prev => ({
      ...prev,
      selectedLayoutId: layoutId || '',
    }));
  };

  const handleEmailFieldChange = (fieldName, isChecked) => {
    setFormData(prev => {
      let updatedSelectedFields;

      if (isChecked) {
        // Add field to selection
        updatedSelectedFields = [...prev.selectedEmailFields, fieldName];
      } else {
        // Remove field from selection
        updatedSelectedFields = prev.selectedEmailFields.filter(f => f !== fieldName);
      }

      // Get all selected field values and join with comma
      const selectedValues = updatedSelectedFields
        .map(field => {
          const emailField = emailFields.find(f => f.field === field);
          return emailField?.value || '';
        })
        .filter(value => value) // Remove empty values
        .join(', ');

      return {
        ...prev,
        selectedEmailFields: updatedSelectedFields,
        to: selectedValues,
      };
    });
  };

  const handleConfigChange = configId => {
    const selectedConfig = configs.find(c => c.id === configId);
    if (selectedConfig) {
      setFormData(prev => ({
        ...prev,
        selectedMailConfig: configId, // ✅ store selected config
      }));
    }
  };

  const handleSubmit = async e => {
    e?.preventDefault?.();
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    setLoading(true);
    setError(null);
    setSuccess(false);
    setMailConfigError(false); // Reset mail config error

    try {
      // Validate required fields
      if (!formData.to || !formData.subject || !formData.body) {
        throw new Error('Please fill in all required fields');
      }

      // Validate mail config is selected
      // Check if selectedMailConfig is a valid UUID (not empty string)
      const hasValidMailConfig = formData.selectedMailConfig &&
        formData.selectedMailConfig.trim() !== '' &&
        formData.selectedMailConfig !== 'null' &&
        formData.selectedMailConfig !== 'undefined';

      if (!hasValidMailConfig) {
        setMailConfigError(true);
        throw new Error('Please select a mail configuration (From)');
      }

      setMailConfigError(false); // Clear error if valid

      // Use memoized recordId
      // recordId is already computed via useMemo

      // Validate recordId and objectName if template is selected
      if (formData.selectedTemplateId && (!recordId || !objectName)) {
        throw new Error('Record ID and Object Name are required when using a template. Please ensure you are sending email from a record detail page.');
      }

      // Prepare attachments - extract IDs from selected attachments
      const attachmentIds = selectedAttachments.map(item => item.id).filter(id => id);
      const filesToUpload = uploadedFiles.filter(f => !f.isUploaded).map(f => f.file);

      // Prepare email data
      const emailData = {
        to: formData.to,
        cc: formData.cc,
        bcc: formData.bcc,
        subject: formData.subject,
        body: formData.body,
        recordId: recordId,
        objectName: objectName,
        selectedMailConfig: formData.selectedMailConfig,
        templateId: formData.selectedTemplateId || null,
        layoutId: formData.selectedLayoutId || null,
        attachmentIds: attachmentIds.length > 0 ? attachmentIds : undefined,
      };

      // Send email API call
      const response = await sendEmail(emailData);

      if (response.success) {
        setSuccess(true);
        // Call the callback with email data
        if (onEmailSent) {
          onEmailSent(response.data);
        }
        // Clear any existing timeout
        if (successTimeout) {
          clearTimeout(successTimeout);
        }
        // Auto-clear success message after 3 seconds
        const timeout = setTimeout(() => {
          setSuccess(false);
        }, 3000);
        setSuccessTimeout(timeout);
        // Reset form after successful send (preserve mail config selection)
        const currentMailConfig = formData.selectedMailConfig;
        setFormData({
          to: '',
          cc: '',
          bcc: '',
          subject: '',
          body: '',
          selectedEmailFields: [],
          selectedOwner: currentUser?.id || '',
          selectedMailConfig: currentMailConfig, // Preserve selected mail config
          selectedTemplateId: '',
          selectedLayoutId: '',
        });
        setSelectedAttachments([]);
        setUploadedFiles([]);

        onHide();
      } else {
        throw new Error(response.message || 'Failed to send email');
      }
    } catch (error) {
      // Show backend error message (e.g. SMTP "Sender verify failed") instead of "Request failed with status code 500"
      const serverMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message;
      setError(serverMessage);
    } finally {
      setLoading(false);
    }
  };

  const sendEmail = async emailData => {
    try {
      const emailApi = new EmailApi();

      // If there are files to upload, use FormData
      const filesToUpload = uploadedFiles.filter(f => !f.isUploaded).map(f => f.file);

      if (filesToUpload.length > 0) {
        const formData = new FormData();
        Object.keys(emailData).forEach(key => {
          if (key !== 'attachmentIds' && emailData[key] !== undefined) {
            if (Array.isArray(emailData[key])) {
              formData.append(key, JSON.stringify(emailData[key]));
            } else {
              formData.append(key, emailData[key]);
            }
          }
        });

        if (emailData.attachmentIds && emailData.attachmentIds.length > 0) {
          formData.append('attachmentIds', JSON.stringify(emailData.attachmentIds));
        }

        filesToUpload.forEach((file, index) => {
          formData.append('files', file);
        });

        const response = await emailApi.sendEmailWithAttachments(formData);
        return response;
      } else {
        const response = await emailApi.sendEmail(emailData);
        return response;
      }
    } catch (error) {
      console.error('Error sending email:', error);
      throw error;
    }
  };

  const handleClose = () => {
    // Preserve mail config selection when closing
    const currentMailConfig = formData.selectedMailConfig;
    setFormData({
      to: '',
      cc: '',
      bcc: '',
      subject: '',
      body: '',
      selectedEmailFields: [],
      selectedOwner: currentUser?.id || '',
      selectedMailConfig: currentMailConfig, // Preserve selected mail config
      selectedTemplateId: '',
      selectedLayoutId: '',
    });
    setSelectedAttachments([]);
    setUploadedFiles([]);
    setError(null);
    setSuccess(false);
    onHide();
  };

  return (
    <>
    <Modal
      show={show}
      onHide={handleClose}
      size='lg'
      centered
      backdrop={true}
      keyboard={true}
      className='email-sender-modal'
      enforceFocus={false}
      restoreFocus={false}
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <i className='fas fa-envelope me-2'></i>
          Send Email
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && (
          <Alert variant='danger' dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert
            variant='success'
            dismissible
            onClose={() => setSuccess(false)}
          >
            Email sent successfully!
          </Alert>
        )}

        <Form onSubmit={handleSubmit}>
          <Row>
            <Col md={6}>
              <Form.Group className='mb-3'>
                <Form.Label>Email Template (Optional)</Form.Label>
                {loadingTemplates ? (
                  <div className="text-muted">
                    <Spinner animation="border" size="sm" className="me-2" />
                    Loading templates...
                  </div>
                ) : (
                  <Form.Select
                    value={formData.selectedTemplateId}
                    onChange={(e) => handleTemplateChange(e.target.value)}
                    disabled={loadingTemplateContent}
                  >
                    <option value=''>No template (use custom subject/body)</option>
                    {templates.map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.name}
                      </option>
                    ))}
                  </Form.Select>
                )}
                <Form.Text className='text-muted'>
                  {templates.length > 0
                    ? `Select a template to auto-fill subject and body. You can edit the content before sending. ${templates.length} template(s) available.`
                    : 'No templates available for this object. You can create templates in the setup section.'}
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className='mb-3'>
                <Form.Label>Branding</Form.Label>
                {loadingLayouts ? (
                  <div className="text-muted">
                    <Spinner animation="border" size="sm" className="me-2" />
                    Loading branding...
                  </div>
                ) : (
                  <Form.Select
                    value={formData.selectedLayoutId}
                    onChange={(e) => handleLayoutChange(e.target.value)}
                    disabled={isBrandingLocked}
                  >
                    <option value=''>No branding</option>
                    {emailLayouts.map((layout) => (
                      <option key={layout.id} value={layout.id}>
                        {layout.name}
                      </option>
                    ))}
                    {/* Keep locked template layout visible even if inactive / not in list */}
                    {isBrandingLocked &&
                      formData.selectedLayoutId &&
                      !emailLayouts.some(
                        l => String(l.id) === String(formData.selectedLayoutId)
                      ) && (
                        <option value={formData.selectedLayoutId}>
                          {selectedLayoutName || 'Template branding'}
                        </option>
                      )}
                  </Form.Select>
                )}
                <Form.Text className='text-muted'>
                  {isBrandingLocked
                    ? `Branding is locked because this template already uses${selectedLayoutName ? ` “${selectedLayoutName}”` : ' an attached layout'}.`
                    : emailLayouts.length > 0
                      ? 'Optional. Choose branding (header/footer) for this email.'
                      : 'No active branding layouts found. Create them under Setup → Email Layout.'}
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className='mb-3'>
                <Form.Label>Email Field Selection</Form.Label>
                {loadingFields ? (
                  <div className="text-muted">
                    <Spinner animation="border" size="sm" className="me-2" />
                    Loading email fields...
                  </div>
                ) : emailFields.length > 0 ? (
                  <div style={{
                    maxHeight: '150px',
                    overflowY: 'auto',
                    border: '1px solid #ced4da',
                    borderRadius: '0.375rem',
                    padding: '8px',
                    backgroundColor: '#fff'
                  }}>
                    {emailFields.map((field, index) => (
                      <Form.Check
                        key={index}
                        type="checkbox"
                        id={`email-field-${index}`}
                        label={`${field.label}: ${field.value}`}
                        checked={formData.selectedEmailFields.includes(field.field)}
                        onChange={(e) => handleEmailFieldChange(field.field, e.target.checked)}
                        style={{ marginBottom: '8px' }}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-muted">
                    No email fields found in this record
                  </div>
                )}
                <Form.Text className='text-muted'>
                  Select one or more email fields from the record to auto-fill recipient
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className='mb-3'>
                <Form.Label>From (Mail Config) *</Form.Label>
                <Form.Select
                  value={formData.selectedMailConfig || ''}
                  onChange={e => {
                    handleConfigChange(e.target.value);
                    setMailConfigError(false); // Clear error when user selects
                  }}
                  required
                  isInvalid={mailConfigError}
                >
                  <option value=''>Select mail configuration</option>
                  {configs.map(config => (
                    <option key={config.id} value={config.id}>
                      {config.name && config.smtp_mail
                        ? `${config.name} (${config.smtp_mail})${config.is_default ? ' (Default)' : ''}`
                        : config.name || config.smtp_mail || config.id}
                    </option>
                  ))}
                </Form.Select>
                {mailConfigError && (
                  <Form.Control.Feedback type="invalid">
                    Please select a mail configuration
                  </Form.Control.Feedback>
                )}
                <Form.Text className='text-muted'>
                  Choose the mail configuration to send from (default is auto-selected)
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={12}>
              <Form.Group className='mb-3'>
                <Form.Label>To *</Form.Label>
                <Form.Control
                  type='email'
                  value={formData.to}
                  onChange={e =>
                    setFormData(prev => ({ ...prev, to: e.target.value }))
                  }
                  placeholder='recipient@example.com'
                  required
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className='mb-3'>
                <Form.Label>CC</Form.Label>
                <Form.Control
                  type='email'
                  value={formData.cc}
                  onChange={e =>
                    setFormData(prev => ({ ...prev, cc: e.target.value }))
                  }
                  placeholder='cc@example.com'
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className='mb-3'>
                <Form.Label>BCC</Form.Label>
                <Form.Control
                  type='email'
                  value={formData.bcc}
                  onChange={e =>
                    setFormData(prev => ({ ...prev, bcc: e.target.value }))
                  }
                  placeholder='bcc@example.com'
                />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className='mb-3'>
            <div className='d-flex justify-content-between align-items-center mb-1'>
              <Form.Label className='mb-0'>Subject *</Form.Label>
              {objectName && (
                <Button
                  variant='outline-secondary'
                  size='sm'
                  type='button'
                  onMouseDown={(e) => handleMergeFieldButtonMouseDown('subject', e)}
                  onClick={() => openMergeFieldSelector('subject')}
                  disabled={loadingTemplateContent || loadingMergePreview}
                >
                  <i className='fas fa-plus me-1'></i>
                  Insert Merge Field
                </Button>
              )}
            </div>
            <Form.Control
              ref={subjectInputRef}
              type='text'
              value={formData.subject}
              onChange={e =>
                setFormData(prev => ({ ...prev, subject: e.target.value }))
              }
              onSelect={saveSubjectSelection}
              onKeyUp={saveSubjectSelection}
              onMouseUp={saveSubjectSelection}
              placeholder='Email subject'
              required
              disabled={loadingTemplateContent}
            />
            {loadingTemplateContent && (
              <Form.Text className='text-muted'>
                <Spinner animation="border" size="sm" className="me-2" />
                Loading template content...
              </Form.Text>
            )}
            {!loadingTemplateContent && (
              <Form.Text className='text-muted'>
                Use Insert Merge Field to add values like {'{{name}}'}. Use Preview Merge Fields to see the resolved email
                {formData.selectedLayoutId ? ' with branding' : ''} without changing your draft.
              </Form.Text>
            )}
          </Form.Group>

          <Form.Group className='mb-3'>
            <div className='d-flex justify-content-between align-items-center mb-1 flex-wrap gap-2'>
              <Form.Label className='mb-0'>Message *</Form.Label>
              <div className='d-flex gap-2 flex-wrap'>
                {objectName && (
                  <Button
                    variant='outline-secondary'
                    size='sm'
                    type='button'
                    onMouseDown={(e) => handleMergeFieldButtonMouseDown('body', e)}
                    onClick={() => openMergeFieldSelector('body')}
                    disabled={loadingTemplateContent}
                  >
                    <i className='fas fa-plus me-1'></i>
                    Insert Merge Field
                  </Button>
                )}
                {recordId && objectName && (
                  <Button
                    variant='outline-primary'
                    size='sm'
                    type='button'
                    onClick={handlePreviewMergeFields}
                    disabled={loadingTemplateContent}
                  >
                    <i className='fas fa-eye me-1'></i>
                    Preview Merge Fields
                  </Button>
                )}
              </div>
            </div>
            <div className='email-editor-container'>
              <CustomRichTextEditor
                ref={bodyEditorRef}
                value={formData.body}
                onChange={content =>
                  setFormData(prev => ({ ...prev, body: content }))
                }
                placeholder='Write your message here...'
                height='200px'
                disabled={loadingTemplateContent}
                showToolbar={true}
                toolbarConfig='full'
              />
            </div>
            {!loadingTemplateContent && (
              <Form.Text className='text-muted'>
                Place the cursor where you want a merge field, then click Insert Merge Field. Your draft keeps {'{{merge}}'} placeholders so you can edit or remove them.
              </Form.Text>
            )}
          </Form.Group>

          {/* Attachments Section */}
          <Form.Group className='mb-3'>
            <Form.Label>Attachments</Form.Label>

            {/* Selected Attachments Display */}
            {selectedAttachments.length > 0 && (
              <div className="mb-2 p-2 bg-light rounded">
                <div className="mb-1">
                  <small className="text-muted">
                    <i className="fas fa-check-circle text-success me-1"></i>
                    {selectedAttachments.length} file{selectedAttachments.length !== 1 ? 's' : ''} selected:
                  </small>
                </div>
                <div style={{
                  maxHeight: '100px',
                  overflowY: 'auto',
                  fontSize: '0.875rem'
                }}>
                  {selectedAttachments.map((item, index) => (
                    <div key={item.id || index} className="text-muted">
                      <i className="fas fa-file me-1"></i>
                      {item.file_name || 'Unknown file'}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Button to Open Attachment Selector */}
            <div className="mb-2">
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => setShowFileSelector(true)}
                disabled={!recordId}
              >
                <i className="fas fa-file me-2"></i>
                {selectedAttachments.length > 0
                  ? `Select Files (${selectedAttachments.length} selected)`
                  : 'Select Files from Related List'}
              </Button>
              {!recordId && (
                <Form.Text className="text-muted d-block mt-1">
                  Record ID is required to select attachments
                </Form.Text>
              )}
            </div>

            {/* File Upload */}
            <div>
              <Form.Label className="mb-2">Upload New Files</Form.Label>
              <Form.Control
                type="file"
                multiple
                onChange={handleFileUpload}
                style={{ marginBottom: '10px' }}
              />
              {uploadedFiles.length > 0 && (
                <div style={{
                  maxHeight: '150px',
                  overflowY: 'auto',
                  border: '1px solid #ced4da',
                  borderRadius: '0.375rem',
                  padding: '8px',
                  backgroundColor: '#f8f9fa',
                  marginTop: '10px'
                }}>
                  {uploadedFiles.map((file) => (
                    <div
                      key={file.id}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '5px',
                        marginBottom: '5px',
                        backgroundColor: '#fff',
                        borderRadius: '4px'
                      }}
                    >
                      <span>
                        <i className="fas fa-file me-2"></i>
                        {file.name} ({formatFileSize(file.size)})
                      </span>
                      <Button
                        variant="link"
                        size="sm"
                        onClick={() => handleRemoveUploadedFile(file.id)}
                        className="text-danger p-0"
                        style={{ textDecoration: 'none' }}
                      >
                        <i className="fas fa-times"></i>
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <Form.Text className='text-muted'>
              Select attachments from the related list or upload new files to attach to the email
            </Form.Text>
          </Form.Group>
        </Form>
      </Modal.Body>

      <Modal.Footer className='email-sender-modal-footer'>
        <Button
          variant='secondary'
          type='button'
          onClick={handleClose}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          variant='primary'
          type='button'
          onClick={handleSubmit}
          disabled={loading || loadingTemplateContent || loadingMergePreview || !formData.to || !formData.subject || !formData.body}
        >
          {loading ? (
            <>
              <Spinner
                as='span'
                animation='border'
                size='sm'
                className='me-2'
              />
              Sending...
            </>
          ) : (
            <>
              <i className='fas fa-paper-plane me-2'></i>
              Send Email
            </>
          )}
        </Button>
      </Modal.Footer>

      {/* File Selector Modal */}
      {recordId && (
        <FileSelector
          show={showFileSelector}
          onHide={() => setShowFileSelector(false)}
          recordId={recordId}
          selectedAttachments={selectedAttachments}
          onSelectionChange={handleAttachmentSelectionChange}
          title="Select Files to Include in Email"
          allowPublicFiles={false}
        />
      )}
    </Modal>

    {typeof document !== 'undefined' && createPortal(
      <Modal
        show={showMergeFieldSelector}
        onHide={() => setShowMergeFieldSelector(false)}
        size='lg'
        centered
        backdrop
        keyboard
        enforceFocus={false}
        restoreFocus={false}
        style={{ zIndex: 1070 }}
        backdropClassName='email-merge-field-backdrop'
        className='email-merge-field-modal'
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Insert Merge Field Into {mergeFieldTarget === 'subject' ? 'Subject' : 'Message'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {objectName ? (
            <>
              <p className='text-muted small'>
                Select a field to insert as {'{{field_name}}'}. For lookup fields you can also type dot notation manually, e.g. {'{{ownerid.email}}'}.
              </p>
              <ObjectFieldSelector
                objectName={objectName}
                enableSearch
                selectionUi='button'
                onSelect={handleMergeFieldSelected}
              />
            </>
          ) : (
            <p className='text-muted mb-0'>Object context is required to load fields.</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={() => setShowMergeFieldSelector(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>,
      document.body
    )}

    {typeof document !== 'undefined' && createPortal(
      <Modal
        show={showMergePreviewModal}
        onHide={handleCloseMergePreview}
        size='lg'
        centered
        backdrop
        keyboard
        enforceFocus={false}
        restoreFocus={false}
        style={{ zIndex: 1080 }}
        backdropClassName='email-merge-preview-backdrop'
        className='email-merge-preview-modal'
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <i className='fas fa-eye me-2'></i>
            Email Preview
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '75vh', overflowY: 'auto' }}>
          <p className='text-muted small mb-3'>
            Preview of merge fields
            {formData.selectedLayoutId ? ' with branding' : ''}
            {' '}for this record. Your draft is unchanged.
          </p>

          {mergePreviewError && (
            <Alert variant='danger' className='mb-3'>
              {mergePreviewError}
            </Alert>
          )}

          {loadingMergePreview ? (
            <div className='text-center py-5'>
              <Spinner animation='border' className='me-2' />
              Resolving preview...
            </div>
          ) : (
            <>
              <div className='mb-3'>
                <div className='text-muted small mb-1'>Subject</div>
                <div className='fw-semibold border rounded p-2 bg-light'>
                  {mergePreviewContent.subject || '—'}
                </div>
              </div>
              <div>
                <div className='text-muted small mb-1'>Message</div>
                <div
                  className='border rounded p-3 bg-white email-merge-preview-body'
                  dangerouslySetInnerHTML={{
                    __html: mergePreviewContent.body || '<div class="text-muted">No message content</div>',
                  }}
                />
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={handleCloseMergePreview}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>,
      document.body
    )}
    </>
  );
};

export default EmailSender;
